import type { NarrativeFact, NarrativeFactKind } from './types';

/**
 * Pure helpers shared by the dialog's components. They live in `lib/` rather
 * than beside the JSX so each component module exports COMPONENTS ONLY — the
 * react-refresh rule the console lints at zero warnings.
 */

/** Locale-aware short instant; the raw ISO stands if it will not parse, because
 *  a timestamp we cannot read is still evidence and must not vanish. */
export function formatInstant(iso: string | null | undefined): string {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The presentation order of the fact kinds: the rule first, then why groups
 * fell out of it, then the counts, then the specifics.
 */
export const FACT_KIND_ORDER: readonly NarrativeFactKind[] = [
  'POLICY',
  'REASON',
  'COUNT',
  'PNR',
  'MONEY',
  'HOTEL',
];

/**
 * Bucket a chapter's facts BY KIND, in a stable order regardless of arrival
 * order. A kind this build has never seen is bucketed rather than dropped —
 * evidence the UI does not recognise is still evidence.
 */
export function factsByKind(
  facts: readonly NarrativeFact[],
): [NarrativeFactKind, NarrativeFact[]][] {
  const buckets = new Map<NarrativeFactKind, NarrativeFact[]>();
  for (const fact of facts) {
    if (!fact || typeof fact !== 'object') continue;
    const kind = (FACT_KIND_ORDER as readonly string[]).includes(fact.kind) ? fact.kind : 'COUNT';
    const bucket = buckets.get(kind);
    if (bucket) bucket.push(fact);
    else buckets.set(kind, [fact]);
  }
  return FACT_KIND_ORDER.filter((k) => buckets.has(k)).map((k) => [
    k,
    buckets.get(k) as NarrativeFact[],
  ]);
}
